import React, { useMemo } from "react";
import { Icon } from "@iconify/react";
import Button from "../../Components/Button";
import Card from "../../Components/Card";
import { Typography } from "../../Components/Typography";
import {
  IDEA_FLOW_STAGE_NAMES,
  getIdeaFlowStageTheme,
} from "../IdeasReview/utils";

const MAX_SCORE = 10;

// Per-rubric colour palette (cycled by index) for the read-only score view
const RUBRIC_PALETTE = [
  { tint: "bg-blue-50", text: "text-blue-600", bar: "bg-blue-500", icon: "lucide:monitor" },
  { tint: "bg-rose-50", text: "text-rose-600", bar: "bg-rose-500", icon: "lucide:shopping-bag" },
  { tint: "bg-indigo-50", text: "text-indigo-600", bar: "bg-indigo-500", icon: "lucide:shopping-cart" },
  { tint: "bg-emerald-50", text: "text-emerald-600", bar: "bg-emerald-500", icon: "lucide:link" },
  { tint: "bg-amber-50", text: "text-amber-600", bar: "bg-amber-500", icon: "lucide:link-2" },
];

const getScoreTone = (score) => {
  const n = Number(score);
  if (!Number.isFinite(n) || n <= 0) return "empty";
  if (n >= 7) return "high";
  if (n >= 4) return "mid";
  return "low";
};

const scoreToneStyles = {
  empty: { bar: "bg-slate-200", badge: "bg-slate-100 text-slate-500 border-slate-200", ring: "ring-slate-200" },
  low: { bar: "bg-orange-500", badge: "bg-orange-50 text-orange-700 border-orange-200", ring: "ring-orange-200" },
  mid: { bar: "bg-amber-500", badge: "bg-amber-50 text-amber-800 border-amber-200", ring: "ring-amber-200" },
  high: { bar: "bg-emerald-500", badge: "bg-emerald-50 text-emerald-700 border-emerald-200", ring: "ring-emerald-200" },
};

const ReviewScoresSection = ({
  activeStage,
  evaluationStages,
  evaluationStagesLoading,
  evaluationStagesError,
  reviewResponses,
  setReviewResponses,
  editedRubrics,
  setEditedRubrics,
  stageReviewOutcomes = {},
  onStageReviewOutcomeChange,
  engagement,
  reviewLocked,
  scoresLocked = false,
  ideaRejected = false,
  isSaving,
  isAdvancing = false,
  hasUnsavedReviewChanges = false,
  canAdvanceStage = false,
  isPrimaryMentor = false,
  secondaryReviewers = [],
  onViewSecondaryScores,
  onAdvanceStage,
  nextStageName = "next stage",
  isFinalStage = false,
  onSaveScores,
  setAnswerModal,
}) => {
  const stage = evaluationStages?.stages?.[activeStage];
  const stageName = stage?.name || IDEA_FLOW_STAGE_NAMES[activeStage] || "Stage";
  const stageTheme = getIdeaFlowStageTheme(stageName);
  const questions = stage?.questions || [];
  // Per-stage "Review Outcome & Feedback" — mandatory before advancing the idea
  // past this stage.
  const stageOutcomeValue = stage
    ? stageReviewOutcomes[String(stage.stageId)] ?? ""
    : "";
  const stageOutcomeMissing = !String(stageOutcomeValue).trim();

  // Scores, notes and the outcome for this stage lock only once the idea has been
  // approved to a later stage (scoresLocked). Saving scores does NOT lock them.
  // (reviewLocked is the separate student/admin read-only view.)
  const inputsLocked = reviewLocked || scoresLocked;

  // Mentor sees a reminder that their assessment is visible to the student and drives
  // progression; students/admins see the indicative-score note.
  const scoreDisclaimer = (
    <div className="flex items-start gap-3 rounded-xl border border-violet-200 bg-violet-50 px-4 py-3">
      <Icon icon="lucide:info" className="h-5 w-5 shrink-0 text-violet-600 mt-0.5" />
      <Typography as="p" className="text-sm text-violet-900 leading-relaxed">
        {ideaRejected
          ? "This idea has been rejected, so its scores, notes and outcome are now read-only."
          : reviewLocked
          ? "The rubric score is only meant to give a view of how ideas fare. The final decision on moving an idea to the next stage rests solely with the mentor and is not based on the score alone."
          : scoresLocked
          ? "This stage has been approved and the idea has moved to a later stage, so its scores, notes and outcome are now locked."
          : "Please note that your assessment will directly impact the student’s idea progression and the scores and notes added by you will be visible to the student."}
      </Typography>
    </div>
  );

  const meta = useMemo(() => {
    const sid = stage ? String(stage.stageId) : "";
    let total = 0;
    let scoredCount = 0;
    let answeredCount = 0;
    questions.forEach((q) => {
      const resp = reviewResponses[sid]?.[String(q.id)] ?? {};
      const s = resp.score;
      if (s != null && s !== "" && !Number.isNaN(Number(s))) {
        total += Number(s);
        scoredCount += 1;
      }
      if ((resp.answer || "").trim()) answeredCount += 1;
    });
    const maxTotal = questions.length * MAX_SCORE;
    const pct = maxTotal > 0 ? Math.round((total / maxTotal) * 100) : 0;
    return { sid, total, maxTotal, scoredCount, answeredCount, pct, count: questions.length };
  }, [stage, questions, reviewResponses]);

  const renderQuestionRow = (q, index) => {
    const stageId = String(stage.stageId);
    const questionId = String(q.id);
    const resp = reviewResponses[stageId]?.[questionId] ?? { answer: "", score: null };
    const scoreVal = resp.score;
    const displayScore = scoreVal == null || scoreVal === "" ? "" : scoreVal;
    const numericScore = Number(displayScore) || 0;
    const tone = getScoreTone(numericScore);
    const styles = scoreToneStyles[tone];
    const pct = MAX_SCORE > 0 ? (numericScore / MAX_SCORE) * 100 : 0;
    const hasAnswer = Boolean((resp.answer || "").trim());
    // A note is mandatory once a score is entered — flag the gap so the mentor can fix it before saving.
    const noteRequired = !inputsLocked && displayScore !== "" && !hasAnswer;

    return (
      <div
        key={q.id || index}
        className={`rounded-xl border p-4 transition-all ${
          noteRequired
            ? "border-amber-300 bg-amber-50/40 ring-1 ring-amber-200"
            : tone === "empty"
            ? "border-slate-200 bg-white"
            : `border-slate-200 bg-white shadow-sm ring-1 ${styles.ring}`
        }`}
      >
        <div className="flex items-start gap-3">
          <div
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-xs font-bold text-white ${stageTheme.badge.split(" ")[0]}`}
          >
            Q{index + 1}
          </div>
          <div className="min-w-0 flex-1 text-left">
            <Typography
              as="p"
              className="text-left text-sm font-medium text-slate-800 leading-snug"
            >
              {q.question}
            </Typography>
            <div className="mt-3 flex items-center gap-3">
              <div className="flex-1">
                <div className="mb-1 flex items-center justify-between text-xs text-slate-500">
                  <span>Score</span>
                  <span className="font-semibold text-slate-700">
                    {displayScore === "" ? "—" : numericScore}/{MAX_SCORE}
                  </span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                  <div
                    className={`h-full rounded-full transition-all duration-300 ${styles.bar}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
              <input
                type="number"
                min="0"
                max={MAX_SCORE}
                value={displayScore}
                readOnly={inputsLocked}
                disabled={inputsLocked}
                onChange={(e) => {
                  const raw = e.target.value;
                  const num =
                    raw === ""
                      ? null
                      : Math.min(MAX_SCORE, Math.max(0, parseInt(raw, 10) || 0));
                  setReviewResponses((prev) => ({
                    ...prev,
                    [stageId]: {
                      ...(prev[stageId] || {}),
                      [questionId]: { ...(prev[stageId]?.[questionId] || {}), score: num },
                    },
                  }));
                }}
                className={`w-14 shrink-0 rounded-lg border px-2 py-1.5 text-center text-sm font-bold focus:outline-none focus:ring-2 focus:ring-violet-500 ${styles.badge} ${
                  inputsLocked ? "cursor-not-allowed opacity-70" : ""
                }`}
                aria-label={`Score for question ${index + 1}`}
              />
            </div>
          </div>
          <button
            type="button"
            className={`flex shrink-0 flex-col items-center gap-1 rounded-lg border px-2.5 py-2 transition-colors ${
              noteRequired
                ? "border-amber-400 bg-amber-100 text-amber-800 hover:bg-amber-200"
                : hasAnswer
                ? "border-violet-300 bg-violet-50 text-violet-700"
                : "border-slate-200 bg-slate-50 text-slate-600 hover:border-violet-200 hover:bg-violet-50"
            }`}
            onClick={() =>
              setAnswerModal({
                stageId,
                questionId,
                question: q.question,
                draft: resp.answer || "",
                readOnly: inputsLocked,
              })
            }
            title={
              inputsLocked
                ? "View mentor notes"
                : noteRequired
                ? "A note is required for this score"
                : hasAnswer
                ? "Edit mentor notes"
                : "Add mentor notes"
            }
          >
            <Icon
              icon={noteRequired ? "lucide:message-square-warning" : "lucide:message-square-text"}
              className="h-4 w-4"
            />
            <span className="text-[10px] font-semibold uppercase tracking-wide">
              {noteRequired ? "Note*" : hasAnswer ? "Notes" : "Add"}
            </span>
          </button>
        </div>
      </div>
    );
  };

  // Read-only result view (student / admin) — matches the Figma "Review & Score" design
  if (reviewLocked) {
    const sid = meta.sid;
    // Scores stay hidden until the mentor approves the idea past this stage, so a
    // review still in progress (partially entered scores) is never shown. Once the
    // stage is approved the full score view below is rendered as usual. The API
    // already withholds the data (stage.scoresVisible === false).
    if (stage && stage.scoresVisible === false) {
      return (
        <div className="flex flex-col gap-6 text-left">
          <Card className="border-slate-200">
            <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-violet-50">
                <Icon icon="lucide:hourglass" className="h-7 w-7 text-violet-600" />
              </div>
              <Typography as="h3" className="text-lg font-bold text-primary-900">
                Review in progress
              </Typography>
              <Typography as="p" className="max-w-md text-sm text-slate-600">
                Your mentor is still reviewing {stageName}. The rubric scores, notes
                and review outcome will be shared with you once this stage is
                approved.
              </Typography>
            </div>
          </Card>
        </div>
      );
    }
    const rubricItems =
      questions.length > 0
        ? questions.map((q) => {
            const resp = reviewResponses[sid]?.[String(q.id)] ?? {};
            return {
              id: q.id,
              name: q.question,
              score: Number(resp.score) || 0,
              answer: resp.answer || "",
              questionId: String(q.id),
            };
          })
        : (editedRubrics || []).map((r) => ({
            id: r.id,
            name: r.name,
            score: Number(r.score) || 0,
            answer: r.answer || "",
            questionId: null,
          }));

    const mentorsCount = (() => {
      if (!stage) return engagement?.numberOfMentors || 0;
      const mentorIds = new Set();
      (stage.questions || []).forEach((q) => {
        (q.reviews || []).forEach((r) => {
          if (r.mentorId != null) mentorIds.add(String(r.mentorId));
        });
      });
      return mentorIds.size || engagement?.numberOfMentors || 0;
    })();

    const isRejected = String(engagement?.status || "").toUpperCase() === "REJECTED";
    const reviewOutcomeMsg = stageOutcomeValue;
    // The idea-level rejection reason is shown in its own box, only on the
    // stage where the idea was rejected (its current stage).
    const rejectionReason = isRejected
      ? engagement?.decisionReason || engagement?.rejectionMessage || ""
      : "";
    const isRejectionStage =
      isRejected &&
      activeStage === Math.max(0, (Number(engagement?.currentStep) || 1) - 1);

    if (evaluationStagesLoading) {
      return (
        <div className="py-10 text-center">
          <Typography as="p" className="text-slate-600">
            Loading review...
          </Typography>
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-6 text-left">
        {scoreDisclaimer}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Average Rubrics Score */}
          <div className="flex flex-col gap-4">
            <Typography as="h3" className="text-lg font-bold text-primary-900">
              Average Rubrics Score
            </Typography>
            {rubricItems.length > 0 ? (
              <div className="flex flex-col gap-3">
                {rubricItems.map((r, i) => {
                  const c = RUBRIC_PALETTE[i % RUBRIC_PALETTE.length];
                  const pct = Math.min(100, Math.max(0, (r.score / MAX_SCORE) * 100));
                  return (
                    <div key={r.id || i} className="flex items-stretch gap-3">
                      <div className="flex-1 rounded-xl border border-slate-200 bg-white p-4">
                        <div className="flex items-center gap-3">
                          <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${c.tint}`}>
                            <Icon icon={c.icon} className={`h-5 w-5 ${c.text}`} />
                          </div>
                          <Typography as="span" className={`flex-1 text-sm font-semibold ${c.text}`}>
                            {r.name}
                          </Typography>
                          <Typography as="span" className={`text-lg font-bold ${c.text}`}>
                            {r.score}
                          </Typography>
                        </div>
                        <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-slate-100">
                          <div className={`h-full rounded-full ${c.bar}`} style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                      <button
                        type="button"
                        disabled={!r.questionId}
                        onClick={() =>
                          r.questionId &&
                          setAnswerModal({
                            stageId: sid,
                            questionId: r.questionId,
                            question: r.name,
                            draft: r.answer || "",
                            readOnly: true,
                          })
                        }
                        className="flex w-14 shrink-0 items-center justify-center rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40"
                        title="View mentor comment"
                      >
                        <Icon icon="lucide:message-square-text" className="h-5 w-5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <Typography as="p" className="text-sm text-slate-400 italic">
                No rubric scores recorded yet.
              </Typography>
            )}
          </div>

          {/* Right: review metrics */}
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="rounded-xl border border-slate-200 bg-white p-5">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-violet-100">
                    <Icon icon="lucide:zap" className="h-5 w-5 text-violet-600" />
                  </div>
                  <Typography as="span" className="text-sm text-primary-900">
                    Number of Mentors
                  </Typography>
                </div>
                <Typography as="p" className="mt-4 text-3xl font-bold text-primary-900">
                  {mentorsCount}
                </Typography>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-5">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-violet-100">
                    <Icon icon="lucide:zap" className="h-5 w-5 text-violet-600" />
                  </div>
                  <Typography as="span" className="text-sm text-primary-900">
                    Overall Score
                  </Typography>
                </div>
                <Typography as="p" className="mt-4 text-3xl font-bold text-primary-900">
                  {meta.total}/{meta.maxTotal || 0}
                </Typography>
              </div>
            </div>

            {isRejectionStage && rejectionReason && (
              <Card className="border-red-200 ring-1 ring-red-200">
                <div className="p-5 flex flex-col gap-3">
                  <div className="flex items-center gap-2">
                    <Icon icon="lucide:circle-x" className="h-5 w-5 text-red-600" />
                    <Typography as="h3" className="text-sm font-bold uppercase tracking-wide text-red-700">
                      Rejection Reason
                    </Typography>
                  </div>
                  <textarea
                    value={rejectionReason}
                    readOnly
                    rows={4}
                    className="w-full rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-900 resize-none cursor-not-allowed focus:outline-none"
                  />
                  <Typography as="p" className="text-xs text-slate-500">
                    Shared by your mentor when the idea was rejected at this stage.
                  </Typography>
                </div>
              </Card>
            )}

            {reviewOutcomeMsg && (
              <Card className="border-slate-200 ring-1 ring-amber-200">
                <div className="p-5 flex flex-col gap-3">
                  <div className="flex items-center gap-2">
                    <Icon icon="lucide:message-square-warning" className="h-5 w-5 text-amber-600" />
                    <Typography as="h3" className="text-sm font-bold uppercase tracking-wide text-slate-600">
                      Review Outcome & Feedback
                    </Typography>
                  </div>
                  <textarea
                    value={reviewOutcomeMsg}
                    readOnly
                    rows={4}
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 resize-none cursor-not-allowed focus:outline-none"
                  />
                  <Typography as="p" className="text-xs text-slate-500">
                    Shared by your mentor with the outcome of this stage review.
                  </Typography>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {scoreDisclaimer}
      {/* Summary header */}
      <div className="rounded-2xl border border-slate-200 p-5 bg-gradient-to-r from-slate-50 via-white to-white">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-4">
            <div
              className={`flex h-12 w-12 items-center justify-center rounded-xl shadow-md ${stageTheme.badge}`}
            >
              <Icon icon="lucide:clipboard-check" className="h-6 w-6" />
            </div>
            <div>
              <Typography as="p" className="text-xs font-bold uppercase tracking-widest text-slate-500">
                Stage review
              </Typography>
              <Typography as="h3" className="text-xl font-bold text-slate-900">
                {stageName} · Rubric scores
              </Typography>
              <Typography as="p" className="text-sm text-slate-600 mt-1">
                {reviewLocked
                  ? "Read-only view of mentor evaluation for this stage."
                  : "Save scores to record your review. When ready, unlock the next stage for the student."}
              </Typography>
            </div>
          </div>
          {canAdvanceStage && (
            <div className="flex flex-col sm:flex-row gap-2 shrink-0">
              <Button
                variant="secondary"
                size="medium"
                className="border-slate-300"
                startIcon={<Icon icon="lucide:save" className="h-5 w-5" />}
                onClick={onSaveScores}
                disabled={
                  isSaving || isAdvancing || evaluationStagesLoading || !questions.length
                }
              >
                {isSaving ? "Saving..." : "Save scores"}
              </Button>
              {!isFinalStage && onAdvanceStage && isPrimaryMentor && (
                <Button
                  variant="primary"
                  size="medium"
                  className="bg-violet-600 hover:bg-violet-700"
                  startIcon={<Icon icon="lucide:arrow-right-circle" className="h-5 w-5" />}
                  onClick={onAdvanceStage}
                  disabled={
                    isAdvancing ||
                    isSaving ||
                    evaluationStagesLoading ||
                    !questions.length ||
                    hasUnsavedReviewChanges ||
                    stageOutcomeMissing
                  }
                  title={
                    hasUnsavedReviewChanges
                      ? "Save your scores before approving to the next stage."
                      : stageOutcomeMissing
                      ? "Add the Review Outcome & Feedback for this stage before approving to the next stage."
                      : undefined
                  }
                >
                  {isAdvancing ? "Approving..." : "Approve to Next Stage"}
                </Button>
              )}
            </div>
          )}
        </div>

        {secondaryReviewers.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-200 pt-4">
            <Typography
              as="span"
              className="text-xs font-semibold uppercase tracking-wide text-slate-500"
            >
              Secondary mentor reviews
            </Typography>
            {secondaryReviewers.map((reviewer) => (
              <button
                key={reviewer.mentorId}
                type="button"
                onClick={() => onViewSecondaryScores?.(reviewer.mentorId)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-violet-200 bg-violet-50 px-3 py-1.5 text-sm font-medium text-violet-700 transition-colors hover:bg-violet-100"
              >
                <Icon icon="lucide:eye" className="h-4 w-4" />
                Review score by {reviewer.name}
              </button>
            ))}
          </div>
        )}

        {canAdvanceStage && isPrimaryMentor && hasUnsavedReviewChanges && !isFinalStage && (
          <div className="mt-3 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
            <Icon icon="lucide:alert-triangle" className="h-4 w-4 shrink-0 text-amber-600" />
            <Typography as="p" className="text-xs text-amber-800">
              Save your scores before you can approve this idea to the next stage.
            </Typography>
          </div>
        )}

        {scoresLocked && !reviewLocked && (
          <div className="mt-3 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
            <Icon icon="lucide:lock" className="h-4 w-4 shrink-0 text-emerald-600" />
            <Typography as="p" className="text-xs text-emerald-800">
              This stage is locked because the idea has been approved to a later
              stage. Scores and notes can no longer be edited.
            </Typography>
          </div>
        )}

        {questions.length > 0 && (
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="rounded-xl bg-white border border-slate-200 px-4 py-3">
              <Typography as="p" className="text-xs text-slate-500 uppercase tracking-wide">
                Overall
              </Typography>
              <Typography as="p" className="text-2xl font-bold text-slate-900 mt-1">
                {meta.total}
                <span className="text-base font-medium text-slate-400">/{meta.maxTotal}</span>
              </Typography>
            </div>
            <div className="rounded-xl bg-white border border-slate-200 px-4 py-3">
              <Typography as="p" className="text-xs text-slate-500 uppercase tracking-wide">
                Progress
              </Typography>
              <Typography as="p" className="text-2xl font-bold text-violet-600 mt-1">
                {meta.pct}%
              </Typography>
            </div>
            <div className="rounded-xl bg-white border border-slate-200 px-4 py-3">
              <Typography as="p" className="text-xs text-slate-500 uppercase tracking-wide">
                Scored
              </Typography>
              <Typography as="p" className="text-2xl font-bold text-slate-900 mt-1">
                {meta.scoredCount}/{meta.count}
              </Typography>
            </div>
            <div className="rounded-xl bg-white border border-slate-200 px-4 py-3">
              <Typography as="p" className="text-xs text-slate-500 uppercase tracking-wide">
                With notes
              </Typography>
              <Typography as="p" className="text-2xl font-bold text-emerald-600 mt-1">
                {meta.answeredCount}/{meta.count}
              </Typography>
            </div>
          </div>
        )}

        {evaluationStagesError && (
          <Typography as="p" className="text-sm text-red-600 mt-3">
            {evaluationStagesError}
          </Typography>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Questions */}
        <div className="lg:col-span-2 flex flex-col gap-3">
          {evaluationStagesLoading ? (
            <Card className="p-8 text-center">
              <Typography as="p" className="text-slate-600">Loading review questions...</Typography>
            </Card>
          ) : questions.length > 0 ? (
            questions.map((q, index) => renderQuestionRow(q, index))
          ) : editedRubrics?.length > 0 ? (
            editedRubrics.map((rubric, index) => {
              const tone = getScoreTone(rubric.score);
              const styles = scoreToneStyles[tone];
              const pct =
                rubric.maxScore > 0 ? ((rubric.score || 0) / rubric.maxScore) * 100 : 0;
              return (
                <div
                  key={rubric.id || index}
                  className="rounded-xl border border-slate-200 bg-white p-4"
                >
                  <div className="flex items-center gap-3">
                    <Icon icon={rubric.icon || "lucide:star"} className="h-5 w-5 shrink-0 text-slate-500" />
                    <div className="min-w-0 flex-1 text-left">
                      <Typography as="p" className="text-left text-sm font-medium text-slate-800">
                        {rubric.name}
                      </Typography>
                      <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div className={`h-full ${styles.bar}`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                    <input
                      type="number"
                      min="0"
                      max={rubric.maxScore || 10}
                      value={rubric.score ?? 0}
                      readOnly={inputsLocked}
                      disabled={inputsLocked}
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        const clamped = Number.isNaN(v)
                          ? 0
                          : Math.min(rubric.maxScore || 10, Math.max(0, v));
                        const next = [...editedRubrics];
                        next[index] = { ...next[index], score: clamped };
                        setEditedRubrics(next);
                      }}
                      className={`w-14 rounded-lg border px-2 py-1 text-center text-sm font-bold ${styles.badge}`}
                    />
                  </div>
                </div>
              );
            })
          ) : (
            <Card className="p-10 text-center">
              <Icon icon="lucide:clipboard-list" className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <Typography as="p" className="text-slate-600">
                No rubric questions are configured for this stage yet.
              </Typography>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="flex flex-col gap-4">
          <Card className="overflow-hidden border-slate-200">
            <div className="bg-gradient-to-br from-violet-600 to-indigo-600 px-5 py-4 text-white">
              <Typography as="p" className="text-xs font-semibold uppercase tracking-wide text-violet-100">
                Overall score
              </Typography>
              <Typography as="p" className="text-3xl font-bold mt-1">
                {meta.total}
                <span className="text-lg font-medium text-violet-200"> / {meta.maxTotal || "—"}</span>
              </Typography>
            </div>
            <div className="p-4 grid grid-cols-1 gap-3">
              <div className="flex items-center gap-3 rounded-lg bg-slate-50 p-3 border border-slate-100">
                <div className="h-10 w-10 rounded-lg bg-violet-100 flex items-center justify-center">
                  <Icon icon="lucide:users" className="h-5 w-5 text-violet-600" />
                </div>
                <div>
                  <Typography as="p" className="text-xs text-slate-500">Mentors reviewed</Typography>
                  <Typography as="p" className="text-lg font-bold text-slate-900">
                    {(() => {
                      if (!stage) return engagement?.numberOfMentors || 0;
                      const mentorIds = new Set();
                      (stage.questions || []).forEach((q) => {
                        (q.reviews || []).forEach((r) => {
                          if (r.mentorId != null) mentorIds.add(String(r.mentorId));
                        });
                      });
                      return mentorIds.size;
                    })()}
                  </Typography>
                </div>
              </div>
            </div>
          </Card>

          <Card
            className={`border-slate-200 ${
              !inputsLocked && stageOutcomeMissing
                ? "ring-1 ring-amber-300"
                : "ring-1 ring-emerald-100"
            }`}
          >
            <div className="p-5 flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <Icon icon="lucide:message-square-warning" className="h-5 w-5 text-amber-600" />
                <Typography as="h3" className="text-sm font-bold uppercase tracking-wide text-slate-600">
                  Review Outcome & Feedback
                  {!inputsLocked && <span className="text-amber-600"> *</span>}
                </Typography>
              </div>
              <textarea
                value={stageOutcomeValue}
                onChange={(e) =>
                  onStageReviewOutcomeChange?.(stage?.stageId, e.target.value)
                }
                readOnly={inputsLocked}
                rows={4}
                className={`w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 resize-none focus:outline-none focus:ring-2 focus:ring-amber-400 ${
                  inputsLocked ? "cursor-not-allowed" : ""
                }`}
                placeholder={
                  inputsLocked
                    ? "No review outcome provided."
                    : "Summarize the outcome of your review and feedback for the student on this stage..."
                }
              />
              <Typography as="p" className="text-xs text-slate-500">
                Visible to the student. Required for every stage before approving the idea to the next stage.
              </Typography>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ReviewScoresSection;
